import React from "react";

import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";

import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import BookmarkRemoveOutlinedIcon from "@mui/icons-material/BookmarkRemoveOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { useNavigate } from "react-router-dom";

/* =========================================================
   STATUS
   ========================================================= */

const statusColor = (status) => {
  if (status === "completed") return "success";
  if (status === "in review") return "warning";
  if (status === "in progress") return "info";

  return "default";
};

const formatStatus = (status) => {
  if (!status) return "Not started";

  return status
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/* =========================================================
   DATE
   ========================================================= */

const formatDate = (date) => {
  if (!date) return null;

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/* =========================================================
   TIME
   ========================================================= */

const formatTrackedTime = (page) => {
  const seconds = Number(page.durationSeconds || 0);
  const minutes = Number(page.durationMinutes || 0);

  let totalMinutes = 0;

  if (seconds > 0) {
    if (seconds < 60) {
      return `${Math.round(seconds)} sec`;
    }

    totalMinutes = seconds / 60;
  } else if (minutes > 0) {
    totalMinutes = minutes;
  } else {
    return null;
  }

  if (totalMinutes < 60) {
    return `${Math.round(totalMinutes)} min`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const remainingMinutes = Math.round(totalMinutes % 60);

  if (!remainingMinutes) {
    return `${hours} hr`;
  }

  return `${hours} hr ${remainingMinutes} min`;
};

/* =========================================================
   CARD
   ========================================================= */

const ActivityPageCard = ({
  page,
  dateLabel,
  dateValue,
  showTrackedTime = false,
  showRemove = false,
  onRemove,
}) => {
  const navigate = useNavigate();

  const formattedDate = formatDate(dateValue);
  const trackedTime = formatTrackedTime(page);

  const hasTrackedTime = Boolean(trackedTime);

  const contentType =
    page.contentType === "handwritten"
      ? "Handwritten"
      : page.contentType === "printed"
        ? "Printed"
        : "Unknown";

  const openPage = () => {
    navigate(`/document/${page.documentId}/page/${page.pageNumber}`);
  };

  return (
    <Paper
      component="article"
      elevation={0}
      sx={{
        p: {
          xs: 2,
          sm: 2.25,
        },

        minHeight: showTrackedTime ? 270 : 245,

        border: "1px solid #dcdcdc",
        borderRadius: 2.5,

        display: "flex",
        flexDirection: "column",

        backgroundColor: "#fff",

        transition:
          "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",

        "&:hover": {
          borderColor: "#bdb58f",
          boxShadow: "0 4px 14px rgba(0,0,0,0.06)",
          transform: "translateY(-1px)",
        },

        "@media (prefers-reduced-motion: reduce)": {
          transition: "none",

          "&:hover": {
            transform: "none",
          },
        },
      }}
    >
      {/* =====================================================
          DOCUMENT
         ===================================================== */}

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.25,
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            width: 38,
            height: 38,

            flexShrink: 0,

            borderRadius: 2,

            backgroundColor: "#fff7db",
            color: "#4a452f",

            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <DescriptionOutlinedIcon
            sx={{
              fontSize: 21,
            }}
          />
        </Box>

        <Box
          sx={{
            minWidth: 0,
            flex: 1,
          }}
        >
          <Typography
            component="h3"
            variant="subtitle1"
            title={page.documentTitle}
            sx={{
              fontWeight: 700,
              color: "#1f2933",
              lineHeight: 1.35,

              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {page.documentTitle || "Untitled Document"}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              mt: 0.35,
              color: "#5f6368",
            }}
          >
            Page {page.pageNumber}
          </Typography>
        </Box>
      </Box>

      {/* =====================================================
          CHIPS
         ===================================================== */}

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.75,
          mt: 1.75,
        }}
      >
        <Chip
          size="small"
          label={formatStatus(page.status)}
          color={statusColor(page.status)}
          sx={{
            fontWeight: 600,
          }}
        />

        <Chip
          size="small"
          variant="outlined"
          label={contentType}
          sx={{
            color: "#444",
            borderColor: "#bdbdbd",
          }}
        />

        {page.submittedByMe && (
          <Chip
            size="small"
            variant="outlined"
            label="Submitted by you"
            sx={{
              color: "#4a452f",
              backgroundColor: "#fffaf0",
              borderColor: "#d7ca91",
              fontWeight: 500,
            }}
          />
        )}
      </Box>

      <Divider
        sx={{
          my: 1.75,
        }}
      />

      {/* =====================================================
          ACTIVITY DETAILS
         ===================================================== */}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.1,
        }}
      >
        {/* DATE */}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <CalendarTodayOutlinedIcon
            aria-hidden="true"
            sx={{
              fontSize: 17,
              color: "#595959",
              flexShrink: 0,
            }}
          />

          <Typography
            variant="body2"
            sx={{
              color: "#444",
              lineHeight: 1.45,
            }}
          >
            <Box
              component="span"
              sx={{
                color: "#5f6368",
              }}
            >
              {dateLabel}:
            </Box>{" "}
            <Box
              component="span"
              sx={{
                fontWeight: 600,
                color: "#303030",
              }}
            >
              {formattedDate || "Not available"}
            </Box>
          </Typography>
        </Box>

        {/* TIME */}

        {showTrackedTime && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <AccessTimeOutlinedIcon
              aria-hidden="true"
              sx={{
                fontSize: 18,
                color: "#595959",
                flexShrink: 0,
              }}
            />

            <Typography
              variant="body2"
              sx={{
                color: "#444",
              }}
            >
              <Box
                component="span"
                sx={{
                  color: "#5f6368",
                }}
              >
                Time contributed:
              </Box>{" "}
              <Box
                component="span"
                sx={{
                  fontWeight: 600,
                  color: "#303030",
                }}
              >
                {hasTrackedTime ? trackedTime : "Not available"}
              </Box>
            </Typography>

            {!hasTrackedTime && (
              <Tooltip
                arrow
                title="This contribution is recorded, but historical timing information is unavailable."
              >
                <Box
                  component="span"
                  tabIndex={0}
                  aria-label="Timing information unavailable"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    borderRadius: "50%",

                    "&:focus-visible": {
                      outline: "2px solid #786d3e",
                      outlineOffset: "2px",
                    },
                  }}
                >
                  <InfoOutlinedIcon
                    sx={{
                      fontSize: 17,
                      color: "#595959",
                    }}
                  />
                </Box>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>

      {/* =====================================================
          ACTIONS
         ===================================================== */}

      <Box
        sx={{
          mt: "auto",
          pt: 2,

          display: "flex",

          flexDirection: {
            xs: "column",
            sm: "row",
          },

          gap: 1,
        }}
      >
        <Button
          variant="outlined"
          endIcon={<ArrowForwardRoundedIcon />}
          onClick={openPage}
          aria-label={`Open ${page.documentTitle || "document"} page ${
            page.pageNumber
          }`}
          sx={{
            flex: 1,

            minHeight: 44,

            color: "#302d1c",
            borderColor: "#b8ad78",

            borderRadius: 2,

            textTransform: "none",
            fontWeight: 600,

            "&:hover": {
              backgroundColor: "#fff7db",
              borderColor: "#786d3e",
            },

            "&:focus-visible": {
              outline: "3px solid #786d3e",
              outlineOffset: "2px",
            },
          }}
        >
          Open Page
        </Button>

        {showRemove && (
          <Button
            variant="text"
            startIcon={<BookmarkRemoveOutlinedIcon />}
            onClick={() => onRemove?.(page)}
            aria-label={`Remove page ${page.pageNumber} from saved pages`}
            sx={{
              minHeight: 44,

              px: 1.5,

              color: "#444",
              textTransform: "none",
              fontWeight: 500,

              borderRadius: 2,

              "&:hover": {
                backgroundColor: "#f5f5f5",
              },

              "&:focus-visible": {
                outline: "3px solid #786d3e",
                outlineOffset: "2px",
              },
            }}
          >
            Remove
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default ActivityPageCard;
